import z from 'zod';
import { api } from '@/shared/api/api';
import { generateSHA256 } from '@/shared/utils/crypto';

const PRESIGNED_URL_STATE = {
  NEW: 'new',
  DUPLICATED: 'duplicated',
} as const;

/**
 * Presigned URL 응답 스키마
 */
const presignedUrlSchema = z.object({
  state: z.enum([PRESIGNED_URL_STATE.NEW, PRESIGNED_URL_STATE.DUPLICATED]),
  presigned_url: z.string().nullable(),
  s3_key: z.string(),
  expires_in: z.number(),
  dk_document_id: z.number(),
  content_type: z.string(),
});

type PresignedUrlResponse = z.infer<typeof presignedUrlSchema>;

/**
 * 1단계: Presigned URL 요청
 */
export const getPresignedUrl = async (
  file: File,
  fileType: 'RECORD' | 'MASTER_DATA'
): Promise<PresignedUrlResponse> => {
  const urlMap = {
    RECORD: 'contractor-records/presigned-url',
    MASTER_DATA: 'master-data/presigned-url',
  };

  const buffer = await file.arrayBuffer();

  const body = {
    file_hash: generateSHA256(new TextDecoder().decode(buffer)),
    file_ext: file.name.split('.').pop(),
    original_file_name: file.name,
    file_size: file.size,
    meta_data: {
      record_type: fileType,
      record_id: generateSHA256(file.name),
      record_name: file.name,
      record_size: file.size,
    },
  };

  return await api.post<PresignedUrlResponse>(urlMap[fileType], body);
};

/**
 * 2단계: S3에 파일 업로드
 */
export const uploadFileToS3 = async (
  file: File,
  presignedUrl: string,
  contentType: string
): Promise<void> => {
  const uploadResponse = await fetch(presignedUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType,
      'x-amz-server-side-encryption': 'AES256',
    },
    body: file,
  });

  if (!uploadResponse.ok) {
    throw new Error('S3에 파일 업로드 중 오류가 발생했습니다.');
  }
};
